/**
 * Comprehensive permission utilities for VLM UI application
 * Handles role-based access control, subscription-based features, and plan status
 */

import React from 'react';
// import { AUTH_TOKEN } from 'constants/AuthConstant';
import { SEC_AUTH_M_TK } from 'constants/AuthConstant';
import { getPermissionInfo, getUserRoleFromToken } from './jwtUtils';
import { getLimitsBySubscription, getDynamicErrorMessage } from 'configs/LimitsConfig';
import {PREMIUM_SUBSCRIPTION_TYPES} from 'constants/PremiumConstant';
/**
 * Permission matrix based on the provided access control table
 */
const PERMISSION_MATRIX = {
  // Features that require plan to be active (DISABLED - all features now based on subscriptionType only)
  REQUIRES_ACTIVE_PLAN: [
    // Removed all features - no longer blocking based on isPlanActive
  ],

  // Features available to all authenticated users (based on subscriptionType limits)
  ALWAYS_AVAILABLE: [
    'GET_SUMMARY',
    'GET_ALL_CONVERSATIONS',
    'UPDATE_USER_ROLE',
    'GET_ALL_USERS',
    'REQUEST_PER_CHANNEL_COUNT',
    'PREVIEW_FILE',
    'GET_REQUESTS',
    'GET_USER_KB_AND_FILES',
    'GET_ALL_KB_NAME',
    'GET_ALL_FILE',
    'AGENTS_INFO',
    'UPDATE_AGENT',
    'DELETE_AGENT',
    'GET_AGENT_REQUESTS',
    'CALL_SUMMARY',
    'CREATE_USERS',
    'UPLOAD_DOCUMENTS',
    'CHANGE_REQUEST_STATUS',
    'VIEW_UPLOADED_FILES',
    'AGENT_DEPLOYMENT',
    'SEARCH',
    'UPDATE_AGENT_STATUS',
    'CHAT_AGENT_DEPLOYMENT',
    'CHAT_AGENT_MANAGEMENT',
    'CHAT_WITH_AGENT'
  ]
};

/**
 * Check if a user has permission for a specific feature
 * @param {string} feature - The feature to check permission for
 * @param {string} token - JWT token (optional, will get from localStorage if not provided)
 * @returns {Object} Permission result with access status and reason
 */
export const checkFeaturePermission = (feature, token = null) => {
  // const authToken = token || localStorage.getItem(AUTH_TOKEN);
  const authToken = token || localStorage.getItem(SEC_AUTH_M_TK);

  if (!authToken) {
    return {
      hasAccess: false,
      reason: 'NO_TOKEN',
      message: 'Authentication required'
    };
  }

  const permissionInfo = getPermissionInfo(authToken);

  // Note: Removed isPlanActive check - all access based on subscriptionType only

  // Feature-specific permission checks based on role and subscription
  const rolePermissions = getRolePermissions(feature, permissionInfo);

  return {
    hasAccess: rolePermissions.hasAccess,
    reason: rolePermissions.reason,
    message: rolePermissions.message,
    permissionInfo
  };
};

/**
 * Get role-based permissions for a specific feature
 * @param {string} feature - The feature to check
 * @param {Object} permissionInfo - User permission information
 * @returns {Object} Role-based permission result
 */
const getRolePermissions = (feature, permissionInfo) => {
  const { role } = permissionInfo;

  // Define role-based access rules (removed requiresActivePlan - all based on subscriptionType only)
  const roleRules = {
    'CREATE_USERS': {
      allowedRoles: ['Admin']
    },
    'UPLOAD_DOCUMENTS': {
      allowedRoles: ['Admin', 'Maker', 'Checker']
    },
    'CHANGE_REQUEST_STATUS': {
      allowedRoles: ['Admin', 'Checker']
    },
    'AGENT_DEPLOYMENT': {
      allowedRoles: ['Admin', 'Maker', 'Checker']
    },
    'UPDATE_AGENT_STATUS': {
      allowedRoles: ['Admin', 'Checker']
    },
    'GET_ALL_CONVERSATIONS': {
      allowedRoles: ['Admin']
    },
    'UPDATE_USER_ROLE': {
      allowedRoles: ['Admin']
    },
    'GET_ALL_USERS': {
      allowedRoles: ['Admin']
    },
    'CHAT_AGENT_DEPLOYMENT': {
      allowedRoles: ['Admin', 'Maker', 'Checker']
    },
    'CHAT_AGENT_MANAGEMENT': {
      allowedRoles: ['Admin', 'Maker', 'Checker']
    },
    'CHAT_AGENT_APPROVAL': {
      allowedRoles: ['Admin', 'Checker']
    },
    'CHAT_WITH_AGENT': {
      allowedRoles: ['Admin', 'Maker', 'Checker']
    }
  };

  const rule = roleRules[feature];

  if (!rule) {
    // If no specific rule, allow all authenticated users
    return {
      hasAccess: true,
      reason: 'ALLOWED',
      message: 'Access granted'
    };
  }

  // Check role permission
  if (!rule.allowedRoles.includes(role)) {
    return {
      hasAccess: false,
      reason: 'ROLE_NOT_ALLOWED',
      message: `This feature is only available to ${rule.allowedRoles.join(', ')} users`
    };
  }

  // Note: Removed plan status check - all access based on subscriptionType only

  return {
    hasAccess: true,
    reason: 'ALLOWED',
    message: 'Access granted'
  };
};

/**
 * Check if user has reached a specific limit
 * @param {string} limitType - Type of limit to check
 * @param {number} currentUsage - Current usage count
 * @param {string} token - JWT token (optional)
 * @returns {Object} Limit check result
 */
export const checkLimit = (limitType, currentUsage, token = null) => {
  // const authToken = token || localStorage.getItem(AUTH_TOKEN);
  const authToken = token || localStorage.getItem(SEC_AUTH_M_TK);

  if (!authToken) {
    return {
      withinLimit: false,
      reason: 'NO_TOKEN',
      message: 'Authentication required'
    };
  }

  const permissionInfo = getPermissionInfo(authToken);
  const limits = getLimitsBySubscription(permissionInfo.subscriptionType);

  const limitKey = `MAX_${limitType}`;
  const maxLimit = limits[limitKey];

  if (maxLimit === undefined) {
    return {
      withinLimit: true,
      reason: 'NO_LIMIT_DEFINED',
      message: 'No limit defined for this feature'
    };
  }

  const withinLimit = currentUsage < maxLimit;

  return {
    withinLimit,
    currentUsage,
    maxLimit,
    reason: withinLimit ? 'WITHIN_LIMIT' : 'LIMIT_EXCEEDED',
    message: withinLimit
      ? `Usage: ${currentUsage}/${maxLimit}`
      : getDynamicErrorMessage(permissionInfo.subscriptionType, limitType)
  };
};

/**
 * Get user's current limits based on their subscription
 * @param {string} token - JWT token (optional)
 * @returns {Object} User's current limits
 */
export const getUserLimits = (token = null) => {
  // const authToken = token || localStorage.getItem(AUTH_TOKEN);
  const authToken = token || localStorage.getItem(SEC_AUTH_M_TK);

  if (!authToken) {
    return null;
  }

  const permissionInfo = getPermissionInfo(authToken);
  return {
    ...getLimitsBySubscription(permissionInfo.subscriptionType),
    permissionInfo
  };
};

/**
 * Check if user can access a route based on their permissions
 * @param {Array} allowedRoles - Array of roles allowed to access the route
 * @param {boolean} requiresPremium - Whether the route requires premium subscription (deprecated - use requiredSubscriptions)
 * @param {string} token - JWT token (optional)
 * @param {Array} requiredSubscriptions - Array of subscription types that can access the route
 * @returns {Object} Route access result
 */
export const checkRouteAccess = (allowedRoles = [], requiresPremium = false, token = null, requiredSubscriptions = []) => {
  // const authToken = token || localStorage.getItem(AUTH_TOKEN);
  const authToken = token || localStorage.getItem(SEC_AUTH_M_TK);

  if (!authToken) {
    return {
      hasAccess: false,
      reason: 'NO_TOKEN',
      redirectTo: '/login'
    };
  }

  const permissionInfo = getPermissionInfo(authToken);

  // Define routes that don't require active plan
  const allowedInactivePlanRoutes = [
    '/app/pages/payment',
    '/app/payment',
    '/app/about',
    '/app/contact',
    '/app/help',
    '/app/settings',
    '/app/profile',
    '/auth',
    '/error-page-1',
    '/error-page-2'
  ];

  const currentPath = window.location.pathname;
  const isExemptRoute = allowedInactivePlanRoutes.some(route => currentPath.startsWith(route));

  // Check if plan is active (required for most features, except exempt routes)
  // Only check for users with subscription types that should have active plans
  const shouldCheckPlanStatus = permissionInfo.subscriptionType &&
    ['Starter', 'Professional', 'Enterprise', 'Pay_As_You_Go'].includes(permissionInfo.subscriptionType);

  // Remove global redirect for inactive plan: allow access, just log
  // if (shouldCheckPlanStatus && !permissionInfo.isPlanActive && !isExemptRoute) {
  //   console.log('Plan status check: Plan inactive, redirecting to payment page');
  //   return {
  //     hasAccess: false,
  //     reason: 'PLAN_INACTIVE',
  //     redirectTo: '/app/pages/payment',
  //     message: 'Your subscription plan is not active. Please activate your plan to continue.'
  //   };
  // }
  if (shouldCheckPlanStatus && !permissionInfo.isPlanActive && !isExemptRoute) {
    // Do not block access, just allow
  }

  // Check role permissions
  if (allowedRoles.length > 0 && !allowedRoles.includes(permissionInfo.role)) {
    return {
      hasAccess: false,
      reason: 'ROLE_NOT_ALLOWED',
      redirectTo: '/error-page-1',
      message: `Access denied. This page is only available to ${allowedRoles.join(', ')} users.`
    };
  }

  // Check subscription requirement (new method)
  if (requiredSubscriptions.length > 0 && !requiredSubscriptions.includes(permissionInfo.subscriptionType)) {
    return {
      hasAccess: false,
      reason: 'SUBSCRIPTION_REQUIRED',
      redirectTo: '/app/pages/payment',
      message: `This feature requires one of the following subscriptions: ${requiredSubscriptions.join(', ')}.`
    };
  }

  // Check premium requirement (legacy support)
  if (requiresPremium && !PREMIUM_SUBSCRIPTION_TYPES.includes(permissionInfo.subscriptionType)) {
    // Special case: Allow admin users to access chat agent features even without premium subscription
    const currentPath = window.location.pathname;
    if (currentPath.includes('/chat-agent') && permissionInfo.role === 'Admin') {
      // Allow admin users to access chat agent features
    } else {
      return {
        hasAccess: false,
        reason: 'PREMIUM_REQUIRED',
        redirectTo: '/app/pages/payment',
        message: 'This feature requires a premium subscription.'
      };
    }
  }

  return {
    hasAccess: true,
    reason: 'ALLOWED',
    permissionInfo
  };
};

/**
 * Higher-order component wrapper for permission checking
 * @param {React.Component} Component - Component to wrap
 * @param {Object} permissions - Permission requirements
 * @returns {React.Component} Wrapped component with permission checking
 */
export const withPermissions = (Component, permissions = {}) => {
  const WithPermissionsComponent = (props) => {
    const { allowedRoles = [], requiresPremium = false, feature = null } = permissions;

    const routeAccess = checkRouteAccess(allowedRoles, requiresPremium);

    if (!routeAccess.hasAccess) {
      // Return appropriate error component or redirect
      return (
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <h3>Access Denied</h3>
          <p>{routeAccess.message}</p>
        </div>
      );
    }

    // If specific feature permission is required, check it
    if (feature) {
      const featureAccess = checkFeaturePermission(feature);
      if (!featureAccess.hasAccess) {
        return (
          <div style={{ padding: '20px', textAlign: 'center' }}>
            <h3>Feature Not Available</h3>
            <p>{featureAccess.message}</p>
          </div>
        );
      }
    }

    return <Component {...props} permissionInfo={routeAccess.permissionInfo} />;
  };
  
  WithPermissionsComponent.displayName = `withPermissions(${
    Component.displayName || Component.name || 'Component'
  })`;

  return WithPermissionsComponent;
};

/**
 * Check chat agent specific permissions
 * @param {string} feature - Chat agent feature to check (e.g., 'chat_agents', 'chat_agent_approval')
 * @param {string} subscriptionType - User's subscription type
 * @returns {boolean} Whether user has access to the feature
 */
export const checkChatAgentPermission = (feature, subscriptionType) => {
  // Chat agents are available for premium users only
  // const premiumSubscriptions = ['Professional', 'Enterprise', 'Starter'];
  const userRole = getUserRoleFromToken(localStorage.getItem(SEC_AUTH_M_TK));

  //Allow admin users to access chat agent 
  if(userRole === 'Admin') {
    return true;
  }

  //for not admin, check subscription type
  switch (feature) {
    case 'chat_agents':
    case 'chat_agent_management':
      // Allow Free users to access chat agents with restricted channels
      // Also allow premium users full access
      return subscriptionType === 'Free' || PREMIUM_SUBSCRIPTION_TYPES.includes(subscriptionType);
    case 'chat_agent_approval':
      // Approval features are for premium users only
      return PREMIUM_SUBSCRIPTION_TYPES.includes(subscriptionType);
    default:
      return false;
  }
};
